/**
 * WorkerTasks — list of process tasks assigned to the worker, with a
 * centered Dialog for completion + form rendering.
 *
 * P0.4 follow-up rewrite (2026-05-16): the legacy dialog only let
 * the worker pick an outcome + comment. With the new
 * /v1/me/tasks/{id}/complete form_data wiring, tasks attached to
 * a ProcessDefinition with a form_schema now render the same form
 * the admin configured (in ProcessDesigner step 2). The dialog
 * mirrors the rendering rules in WorkerSubmitRequest.tsx so the
 * UX is consistent across submit-request and complete-task paths.
 *
 * Dialog (not drawer) per svdy-ui.md §5 invariant: "Default is
 * <Dialog>. A drawer is appropriate only for read-only context
 * inspection." This screen holds a form + Submit/Cancel actions,
 * so it lives in a centered Dialog. (Earlier 2026-05-16 build used
 * a 480px right-drawer with a "deliberate exception" justification;
 * removed 2026-05-22 to keep all worker-portal mutation surfaces
 * consistent.)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl,
  InputLabel, InputAdornment, MenuItem, Paper, Select, Snackbar, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { ApiError, meApi } from '../../api';
import type { MeOrganizationLocation } from '../../api/me';
import ProcessFormResourceValue, {
  safeAttachmentFilename,
  type ProcessAttachmentMeta,
} from './ProcessFormResourceValue';
import { downloadBlob } from '../../utils/downloadFile';
import { activeLocale } from '../../utils/datetime';
import WorkerListToolbar from '../../components/worker/WorkerListToolbar';
import { translateApiError } from '../../i18n/translateApiError';
import { seedStatusFromUrl } from '../../utils/seedStatusFromUrl';
import {
  localizedProcessFieldLabel,
  localizedProcessFieldOptions,
  processFormFieldKey,
  processFormFieldKind,
} from './processFormContract';
import { getSystemTaskTitle, translateOutcome } from '../Processes/processDisplay';
import SubmittedApplication from '../../components/process/SubmittedApplication';

interface FormFieldProperty {
  label?: string;
  placeholder?: string;
  required?: boolean;
  options?: string[] | { label: string; value: string }[];
  [k: string]: unknown;
}

interface FormField {
  id?: string;
  type?: string;
  label?: string;
  required?: boolean;
  properties?: FormFieldProperty;
}

interface Task {
  id: string;
  instance_id?: string | null;
  definition_id?: string | null;
  title: string;
  /** The step this task is on; with `definition_system_key` it localizes a BUILT-IN step's title. */
  node_id?: string | null;
  /** Set only for SVDY's built-in definitions; a tenant-designed task title is shown as designed. */
  definition_system_key?: string | null;
  description?: string | null;
  priority: string;
  status: string;
  due_date?: string | null;
  completed_at?: string | null;
  outcome?: string | null;
  progress: number;
  form_data?: Record<string, unknown> | null;
  form_schema?: { system_key?: string | null; fields?: FormField[] } | null;
  request_data?: Record<string, unknown> | null;
  request_form_schema?: { system_key?: string | null; fields?: FormField[] } | null;
  attachments?: ProcessAttachmentMeta[] | null;
  /** The organization's currency, for a `currency_field` that does not carry its own. */
  organization_currency?: string | null;
  created_at: string;
}

const fieldKey = processFormFieldKey;

const isFieldRequired = (f: FormField): boolean =>
  Boolean(f.properties?.required ?? f.required);

const STATUS_LABEL: Record<string, string> = {
  pending: 'worker:tasks.statusPending',
  in_progress: 'worker:tasks.statusInProgress',
  review: 'worker:tasks.statusInReview',
  completed: 'worker:tasks.statusCompleted',
  cancelled: 'worker:tasks.statusCancelled',
};

const ACTIONABLE_TASK_STATUSES = ['pending', 'in_progress', 'review'];

const PRIORITY_COLOR: Record<string, 'default' | 'info' | 'warning' | 'error'> = {
  low: 'info',
  medium: 'default',
  high: 'warning',
  critical: 'error',
};

const WorkerTasks: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loadError, setLoadError] = useState(false);
    /**
   * The status filter can be SEEDED from `?status=`, because a click on a COUNT must land on the list
   * that count describes. The Overview's pending card links here with `?status=pending`; the sidebar
   * entry links without it and keeps this page's own default.
   *
   * Seed only — the toolbar owns the value afterwards. Syncing both ways would make the URL and the
   * control two sources of truth for one piece of state (svdy-rules §10.5b), and the `?compose=1`
   * precedent in this repo consumes-and-clears for the same reason.
   */
  const [statusFilter, setStatusFilter] = useState<'pending' | 'completed' | 'all'>(() => {
    const seeded = seedStatusFromUrl<'pending' | 'completed' | 'all'>('pending');
    return seeded;
  });
  // Search: this list has Title and Form, both free text. Owner 2026-09-04 flagged the
  // attendance/process search asymmetry on Approvals; the rule that resolves it is that search
  // belongs to any list with a free-text column, and this page had none at all.
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Task | null>(null);
  const [outcome, setOutcome] = useState('approved');
  const [comment, setComment] = useState('');
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [locations, setLocations] = useState<MeOrganizationLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsError, setLocationsError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('success');

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      // Fetch all tasks assigned to me; filter client-side. Lets a
      // single 'Pending' filter capture both 'pending' AND 'in_progress'
      // task statuses (engine spawns tasks as 'pending', UI flips to
      // 'in_progress' when worker opens). Backend's status= query
      // param only does exact match.
      const data = (await meApi.tasks.list({})) as Task[];
      setTasks(Array.isArray(data) ? data : []);
    } catch {
      // Preserve last-known-good rows; an API failure is not an empty task list.
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const requestFields = useMemo<FormField[]>(
    () => selected?.request_form_schema?.fields ?? [],
    [selected],
  );
  /**
   * A COMPLETION FIELD THE REQUEST ALREADY ASKED FOR IS NOT A COMPLETION FIELD (2026-09-08).
   *
   * Reported on the phone and identical here: an approver was shown `Vendor *`, `Quantity *`,
   * `Estimated Cost *` as empty required inputs, directly beneath the same values rendered read-only from
   * the request context — asked to re-type what they were reviewing.
   *
   * The server's `task_form_schema` is a PROJECTION of the request form (it strips references, uploads and
   * static text and keeps the rest), and there is no task node: `engine._execute_node` dispatches only
   * `approval`, `condition`, `notification`, `cc`, `end`, so every `ProcessTask` is an approval and has no
   * form to fill.
   *
   * Filtered on the client rather than emptied on the server because `processes/service.py` validates
   * submitted completion data against that same projection; widening a validation contract to fix a
   * rendering defect is the wrong trade. Only duplicates are dropped, so a genuine task-specific form
   * would still render.
   */
  const completionFields = useMemo<FormField[]>(() => {
    const requestIds = new Set(requestFields.map((f, i) => f?.id ?? `field_${i}`));
    return (selected?.form_schema?.fields ?? []).filter(
      (f, i) => !requestIds.has(f?.id ?? `field_${i}`),
    );
  }, [selected, requestFields]);

  useEffect(() => {
    if (!completionFields.some((field) => processFormFieldKind(field.type) === 'location')) return;
    let cancelled = false;
    setLocationsLoading(true);
    setLocationsError(false);
    meApi.organizationLocations.listActive()
      .then((items) => { if (!cancelled) setLocations(items); })
      .catch(() => { if (!cancelled) { setLocations([]); setLocationsError(true); } })
      .finally(() => { if (!cancelled) setLocationsLoading(false); });
    return () => { cancelled = true; };
  }, [completionFields]);

  // Client-side status filter — backend returns all assigned tasks,
  // we group statuses for SMB-friendly labels:
  //   Pending → 'pending' + 'in_progress' + 'review' (anything actionable)
  //   Completed → 'completed' + 'cancelled' (anything closed)
  //   All → no filter
  const filteredTasks = useMemo(() => {
    if (statusFilter === 'all') return tasks;
    if (statusFilter === 'pending') {
      return tasks.filter((tk) => ACTIONABLE_TASK_STATUSES.includes(tk.status));
    }
    if (statusFilter === 'completed') {
      return tasks.filter((tk) => ['completed', 'cancelled'].includes(tk.status));
    }
    return tasks;
  }, [tasks, statusFilter]);

  const visibleTasks = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return filteredTasks;
    return filteredTasks.filter((tk) => {
      const t2 = tk as { title?: string; definition_name?: string; form_name?: string };
      return [t2.title, t2.definition_name, t2.form_name]
        .filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [filteredTasks, query]);

  const openDetail = (t: Task) => {
    setSelected(t);
    setOutcome('approved');
    setComment('');
    // Pre-fill form_data from task.form_data when re-opening a
    // completed task (read-only); fresh empty object for pending.
    if (ACTIONABLE_TASK_STATUSES.includes(t.status)) {
      setFormData({});
    } else {
      setFormData((t.form_data as Record<string, unknown>) ?? {});
    }
    setSubmitError(null);
  };

  const closeDetail = () => {
    if (saving) return;
    setSelected(null);
  };

  const updateField = (key: string, value: unknown) =>
    setFormData((prev) => ({ ...prev, [key]: value }));

  const canSubmit = useMemo(() => {
    if (!selected || !ACTIONABLE_TASK_STATUSES.includes(selected.status)) return false;
    if (!outcome) return false;
    if (completionFields.some((field) => processFormFieldKind(field.type) === 'attachment')) return false;
    for (let i = 0; i < completionFields.length; i++) {
      const f = completionFields[i];
      if (!isFieldRequired(f)) continue;
      const key = fieldKey(f, i);
      const v = formData[key];
      if (v == null || (typeof v === 'string' && v.trim() === '')) return false;
    }
    return true;
  }, [selected, outcome, completionFields, formData]);

  const handleComplete = async () => {
    if (!selected || !canSubmit) return;
    setSaving(true);
    setSubmitError(null);
    try {
      // Build form_data with proper types (Number → number, others
      // pass through). Empty values are omitted so the backend's
      // _is_empty check matches the frontend's.
      const payload: Record<string, unknown> = {};
      completionFields.forEach((f, i) => {
        const key = fieldKey(f, i);
        const v = formData[key];
        if (v == null || v === '') return;
        const kind = processFormFieldKind(f.type);
        if ((kind === 'number' || kind === 'currency') && typeof v === 'string') {
          const n = Number(v);
          payload[key] = Number.isFinite(n) ? n : v;
        } else {
          payload[key] = v;
        }
      });
      await meApi.tasks.complete(selected.id, {
        outcome,
        comment: comment || undefined,
        form_data: Object.keys(payload).length > 0 ? payload : undefined,
      });
      setToastSeverity('success');
      setToast(t('worker:tasks.toastCompleted'));
      setSelected(null);
      await fetchTasks();
    } catch (e) {
      setSubmitError(
        e instanceof ApiError ? translateApiError(e, t) : t('worker:tasks.completeFailed'),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadAttachment = async (attachment: ProcessAttachmentMeta) => {
    try {
      const blob = await meApi.processAttachments.download(attachment.id);
      downloadBlob(blob, safeAttachmentFilename(attachment.file_name));
    } catch {
      setToastSeverity('error');
      setToast(t('worker:processResources.downloadFailed'));
    }
  };

  if (loading && tasks.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const renderField = (f: FormField, idx: number) => {
    const key = fieldKey(f, idx);
    const label = localizedProcessFieldLabel(selected?.form_schema, f, idx, t);
    const required = isFieldRequired(f);
    const kind = processFormFieldKind(f.type);
    const currencyCode = String(f.properties?.currency ?? 'USD').toUpperCase();
    const value = (formData[key] as string | number | undefined) ?? '';
    const readOnly = !selected || !ACTIONABLE_TASK_STATUSES.includes(selected.status);

    if (kind === 'attachment') {
      return <Alert severity="error" key={key}>{t('worker:tasks.attachmentsUnsupported')}</Alert>;
    }

    if (kind === 'location') {
      if (readOnly) {
        return <ProcessFormResourceValue key={key} fieldId={key} fieldType={f.type} value={formData[key]} t={t} />;
      }
      return (
        <TextField select key={key} fullWidth size="small" label={label} required={required}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => updateField(key, event.target.value)}
          disabled={locationsLoading || locationsError}
          error={locationsError}
          helperText={locationsError ? t('worker:submitRequest.locationsLoadFailed') : t('worker:submitRequest.locationHelper')}>
          {locations.map((location) => (
            <MenuItem key={location.id} value={location.id}>
              {location.name}{location.address ? ` — ${location.address}` : ''}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (kind === 'unsupported') {
      return <Alert severity="error" key={key}>{t('worker:tasks.completeFailed')}</Alert>;
    }

    if (kind === 'select') {
      const opts = localizedProcessFieldOptions(selected?.form_schema, f, t);
      return (
        <FormControl fullWidth size="small" key={key} required={required}>
          <InputLabel>{label}</InputLabel>
          <Select
            value={value as string}
            label={label}
            disabled={readOnly}
            onChange={(e) => updateField(key, e.target.value)}
          >
            {opts.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <TextField
        key={key}
        fullWidth
        size="small"
        label={label}
        required={required}
        value={value}
        type={kind === 'date' ? 'date' : (kind === 'number' || kind === 'currency') ? 'number' : 'text'}
        multiline={kind === 'textarea'}
        minRows={kind === 'textarea' ? 3 : undefined}
        InputLabelProps={kind === 'date' ? { shrink: true } : undefined}
        InputProps={{
          ...(readOnly ? { readOnly: true } : {}),
          ...(kind === 'currency' ? {
            endAdornment: <InputAdornment position="end">{currencyCode}</InputAdornment>,
          } : {}),
        }}
        placeholder={f.properties?.placeholder as string | undefined}
        onChange={(e) => updateField(key, e.target.value)}
      />
    );
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      py: '12px',
      width: '100%',
    }}>
      {/* Header */}
      <Box sx={{
        width: '100%', display: 'flex',
        justifyContent: 'space-between', alignItems: 'center',
        mb: 1.5, flexWrap: 'wrap', gap: 1,
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
            {t('worker:tasks.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('worker:tasks.tasksSubtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Moved OUT of the title row into the one position every worker-portal list uses, and
          "All" moved to the FRONT. It was last here and first on both requests tabs — the same
          control reading in a different order one click apart. */}
      <WorkerListToolbar
        set="assigned"
        value={statusFilter}
        onChange={(v) => setStatusFilter(v as typeof statusFilter)}
        search={query}
        onSearchChange={setQuery}
      />

      {loadError && (
        <Alert
          severity="error"
          sx={{ width: '100%', mb: 1.5 }}
          action={<Button color="inherit" size="small" onClick={fetchTasks}>{t('common:actions.retry')}</Button>}
        >
          {t('common:feedback.loadFailed')}
        </Alert>
      )}

      {visibleTasks.length === 0 && !loadError ? (
        <Paper elevation={0} sx={{
          width: '100%', p: 5, textAlign: 'center',
          borderRadius: 2, border: '1px solid', borderColor: 'divider',
        }}>
          <Typography variant="body2" color="text.secondary">
            {t('worker:tasks.noTasksFound')}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            {statusFilter === 'pending'
              ? t('worker:tasks.noPendingHint')
              : t('worker:tasks.noTasksHint')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{
          width: '100%', borderRadius: 2, overflow: 'hidden',
          border: '1px solid', borderColor: 'divider', boxShadow: 'none',
        }}>
          <Table size="small">
            <TableHead sx={{
              bgcolor: 'grey.100',
              '& .MuiTableCell-root': { fontWeight: 600, fontSize: '0.75rem' },
            }}>
              <TableRow>
                <TableCell>{t('worker:tasks.colTitle')}</TableCell>
                <TableCell>{t('worker:tasks.colForm')}</TableCell>
                <TableCell>{t('worker:tasks.colPriority')}</TableCell>
                <TableCell>{t('worker:tasks.colDue')}</TableCell>
                <TableCell align="center">{t('worker:tasks.colStatus')}</TableCell>
                <TableCell align="right">{t('worker:tasks.colActions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleTasks.map((tk) => {
                const fieldCount = tk.form_schema?.fields?.length ?? 0;
                return (
                  <TableRow
                    key={tk.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openDetail(tk)}
                  >
                    <TableCell>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {getSystemTaskTitle(t, tk, tk.node_id, tk.title)}
                      </Typography>
                      {tk.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontSize: '0.65rem' }}
                          noWrap
                        >
                          {tk.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {fieldCount > 0 ? (
                        <Chip
                          label={t('worker:requests.fieldCount', { count: fieldCount })}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: 1, height: 18, fontSize: '0.65rem' }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tk.priority}
                        size="small"
                        color={PRIORITY_COLOR[tk.priority] ?? 'default'}
                        variant="outlined"
                        sx={{ borderRadius: 1, height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {tk.due_date ? new Date(tk.due_date).toLocaleDateString(activeLocale()) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={t(STATUS_LABEL[tk.status] ?? '', { defaultValue: tk.status })}
                        size="small"
                        color={
                          tk.status === 'completed'
                            ? 'success'
                            : tk.status === 'cancelled'
                              ? 'default'
                              : tk.status === 'pending' || tk.status === 'in_progress'
                                ? 'warning'
                                : 'default'
                        }
                        variant="outlined"
                        sx={{ borderRadius: 1, height: 18, fontSize: '0.65rem' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {ACTIONABLE_TASK_STATUSES.includes(tk.status) && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => { e.stopPropagation(); openDetail(tk); }}
                          sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.75rem', py: 0 }}
                        >
                          {fieldCount > 0 ? t('worker:tasks.openForm') : t('worker:tasks.complete')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Task detail + completion form — centered Dialog per
          svdy-ui §5 (forms / mutations live in Dialog, not Drawer). */}
      <Dialog
        open={!!selected}
        onClose={() => !saving && closeDetail()}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        {selected && (
          <>
            {/* paddingInlineEnd, not pr: in the `ar` RTL cache a physical `pr`
                flips to padding-LEFT and moves the title off the dialog's left
                axis. The clearance is for the absolutely-positioned close X, so
                it belongs on the inline END side in both directions. */}
            <DialogTitle sx={{ py: 2, paddingInlineEnd: 6 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, fontSize: '1rem' }}
                  noWrap
                >
                  {getSystemTaskTitle(t, selected, selected.node_id, selected.title)}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
                  <Chip
                    label={selected.priority}
                    size="small"
                    variant="outlined"
                    color={PRIORITY_COLOR[selected.priority] ?? 'default'}
                    sx={{ height: 20, fontSize: '0.7rem', textTransform: 'capitalize' }}
                  />
                  <Chip
                    label={t(STATUS_LABEL[selected.status] ?? '', { defaultValue: selected.status })}
                    size="small"
                    color={
                      selected.status === 'completed' ? 'success'
                        : selected.status === 'pending' ? 'warning'
                          : 'default'
                    }
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                  {selected.due_date && (
                    <Typography variant="caption" color="text.secondary">
                      Due {new Date(selected.due_date).toLocaleDateString(activeLocale())}
                    </Typography>
                  )}
                </Stack>
              </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ py: 3 }}>
              <Stack spacing={2}>
                {selected.description && (
                  <Typography variant="body2" color="text.secondary">
                    {selected.description}
                  </Typography>
                )}

                {ACTIONABLE_TASK_STATUSES.includes(selected.status) && (
                  <Alert severity="info" variant="outlined" sx={{ fontSize: '0.8rem' }}>
                    {t('worker:tasks.formHint')}
                  </Alert>
                )}

                {submitError && (
                  <Alert
                    severity="error"
                    variant="outlined"
                    sx={{ fontSize: '0.8rem' }}
                    onClose={() => setSubmitError(null)}
                  >
                    {submitError}
                  </Alert>
                )}

                {requestFields.length > 0 && selected.request_data && (
                  <SubmittedApplication
                    formData={selected.request_data}
                    formSchema={selected.request_form_schema}
                    attachments={selected.attachments}
                    organizationCurrency={selected.organization_currency}
                    onDownload={handleDownloadAttachment}
                    t={t}
                  />
                )}

                {/* Completion form schema fields */}
                {completionFields.length > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1, fontWeight: 600 }}
                    >
                      {t('worker:tasks.formSection')}
                    </Typography>
                    {/* Stack, not <Grid container> — every field is full
                        width here, so the Grid added nothing but its negative
                        margins, which inset these fields 12px relative to the
                        Decision block below (same class as the attendance
                        request dialog, svdy-ui §11.1). */}
                    <Stack spacing={1.5}>
                      {completionFields.map((f, i) => (
                        <Box key={fieldKey(f, i)}>{renderField(f, i)}</Box>
                      ))}
                    </Stack>
                  </Box>
                )}

                {ACTIONABLE_TASK_STATUSES.includes(selected.status) && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1, fontWeight: 600 }}
                    >
                      {t('worker:tasks.decisionSection')}
                    </Typography>
                    <Stack spacing={1.5}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{t('worker:tasks.labelOutcome')}</InputLabel>
                        <Select
                          value={outcome}
                          label={t('worker:tasks.labelOutcome')}
                          onChange={(e) => setOutcome(e.target.value as string)}
                        >
                          <MenuItem value="approved">{t('common:status.approved', { defaultValue: 'Approved' })}</MenuItem>
                          <MenuItem value="rejected">{t('common:status.rejected', { defaultValue: 'Rejected' })}</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('worker:tasks.labelComment')}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        multiline
                        minRows={2}
                      />
                    </Stack>
                  </Box>
                )}

                {!ACTIONABLE_TASK_STATUSES.includes(selected.status) && selected.outcome && (
                  <Box>
                    {/* The label was the hardcoded English string `Outcome`, so all nine locales showed
                        it in English (i18n-nine-locales §3), and the VALUE was the raw enum — an
                        approver read `approved` in lower case while every status beside it was a
                        translated chip (owner, 2026-09-09, screenshot 05). `translateOutcome` and the
                        label key both already existed; this call site never learned. */}
                    <Typography variant="caption" color="text.secondary">
                      {t('worker:tasks.labelOutcome')}
                    </Typography>
                    <Typography variant="body2">{translateOutcome(t, selected.outcome)}</Typography>
                  </Box>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ py: 2 }}>
              <Button
                onClick={closeDetail}
                disabled={saving}
                sx={{ borderRadius: 1, textTransform: 'none' }}
              >
                {ACTIONABLE_TASK_STATUSES.includes(selected.status) ? t('common:actions.cancel') : t('common:actions.close')}
              </Button>
              {ACTIONABLE_TASK_STATUSES.includes(selected.status) && (
                <Button
                  onClick={handleComplete}
                  variant="contained"
                  disabled={!canSubmit || saving}
                  sx={{ borderRadius: 1, textTransform: 'none' }}
                >
                  {saving ? <CircularProgress size={18} /> : t('worker:tasks.submitTask')}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={toastSeverity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkerTasks;
